// index.tsx
import * as React from 'react';
import { PageProps } from '../../../types/navigationProperties';
import { PaginationRequest } from '../../../types/pagination';
import { GuitarServiceGallery, getAllGuitarServiceGalleries } from '../../../services/GuitarServiceGalleriesServiceProvider';
import { useTranslation } from 'react-i18next';
import { Grid, Box, Container, TablePagination } from '@mui/material';
import Footer from '../../../components/Footer';
import PageHeader from '../../../components/PageHeader';
import CardImageGallery from '../../../components/CardImageGallery';
import { displayErrorToast } from '../../../utils/toasts';
import LoaderSpinner from '../../../components/LoaderSpinner';
import { LightShotSingle } from '../../../components/LightShotSingle';
import { GalleryImage } from '../../../types/galleryImage';
import guitarServicePage from '../../../assets/img/guitar_service_page.jpg';
import { SetButtonCallbackFunction } from '../../../types/callback';
import { SEO } from '../../../components/SEO';
import { MetaTag } from '../../../services/SEOServiceProvider';
import aboutPage from '../../../assets/img/about_page.jpg';
import { ColorfulText } from '../../../components/ColorfulText';


export default function GuitarServiceList({ props, setPageName, imageSize, metaTags }: { props: PageProps, setPageName: SetButtonCallbackFunction, imageSize: string, metaTags: MetaTag[] }) {

    const { t } = useTranslation();
    const headingProps = {title: t("guitar_service.title"), description: undefined};
    const language = props.language;
    const [loading, setLoading] = React.useState(true);
    const [page, setPage] = React.useState(0);
    const [data, setData] = React.useState<GuitarServiceGallery[]>();
    const [total, setTotal] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(8);
    const pageSizeOptions: number[] = [4, 8, 12, 16];
    const [pagination, setPagination] = React.useState<PaginationRequest>({page: page, page_size: pageSize});
    const [open, setOpen] = React.useState(false);
    const [selectedImage, setSelectedImage] = React.useState<GalleryImage>();
    const [metaTag, setMetaTag] = React.useState<MetaTag>();

    const handlePaginationChange = () => {
        setPagination({page: page, page_size: pageSize});
    }

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
        handlePaginationChange();
    };

    const handleChangePageSize = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setPageSize(parseInt(event.target.value, 8));
        setPage(0);
        handlePaginationChange();
    };

    const handleOpenLightShot = (image: string, name: string) => {
        let item: GalleryImage = { src: image, name: name };
        setSelectedImage(item);
        setOpen(true);
    }

    React.useEffect(() => {
        setPageName('guitar-service');
        let metaTagData = metaTags !== undefined && metaTags.filter((item: MetaTag) => item.page === 'guitar-service' );
        if(metaTagData && metaTagData.length > 0) {
            let guitarServicePageMetaTag: MetaTag = {...metaTagData[0]} as MetaTag;
            guitarServicePageMetaTag.url = window.location.href;
            guitarServicePageMetaTag.image = aboutPage;
            setMetaTag(guitarServicePageMetaTag);
        }

        console.log(pagination);
        (async () => {
            try {
                setLoading(true);
                const guitarServiceData = await getAllGuitarServiceGalleries(pagination, imageSize);
                console.log(guitarServiceData);
                setData(guitarServiceData.results);
                setTotal(guitarServiceData.count);
            } catch (error) {
                displayErrorToast(error);
            } finally {
                setLoading(false);
            }
        })();
    }, [language, pagination, setPageName, imageSize, metaTags]);

    return loading ? (
        <div>
            <SEO metaTag={metaTag} />
            <LoaderSpinner loading={loading} />
        </div>
    ) : (
        <Box>
            <SEO metaTag={metaTag} />
            <LightShotSingle image={selectedImage} opened={open} setOpenLightShot={setOpen} />
            <Box>
                <PageHeader props={headingProps} />
            </Box>
            <Box sx={{ py: '40px', px: '20px', paddingTop: { xs: '0px', sm: '0px', md: '40px' }, bgcolor: 'primary.light' }}
                 style={{ width: '100%', height: '100%' }}>
                <Container>
                    <Box sx={{ marginTop: { sm: '8px', md: '9px', lg: '11px' } }}>
                        <ColorfulText style={{
                                textAlign: 'center',
                                fontSize: { sm: '14px', md: '16px', lg: '18px' } }}
                                      text={t("guitar_service.description.1")}
                                      randomChars={true} skipChar={[]} useColor={'random'} />
                    </Box>
                    <Box sx={{ my: { sm: '8px', md: '9px', lg: '11px' } }}>
                        <ColorfulText style={{
                                textAlign: 'center',
                                fontSize: { sm: '14px', md: '16px', lg: '18px' } }}
                                      text={t("guitar_service.description.2")}
                                      randomChars={true} skipChar={[]} useColor={'random'} />
                    </Box>
                </Container>
                <TablePagination component="div"
                              count={total}
                              page={page}
                              sx={{ '>div': {
                                  alignContent: 'flex-start',
                                  flexWrap: 'wrap',
                              }}}
                              onPageChange={handleChangePage}
                              rowsPerPage={pageSize}
                              labelRowsPerPage={t('table.pagination.label_rows_per_page')}
                              labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} ${t('table.pagination.of')} ${
                                        count !== -1 ? count : `${t('table.pagination.more_than')} ${to}`
                                    }`
                              }
                              getItemAriaLabel={(type) => t(`table.pagination.aria_label_${type}`)}
                              rowsPerPageOptions={pageSizeOptions}
                              onRowsPerPageChange={handleChangePageSize} />
                <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }} sx={{
                                    display: 'flex',
                                    position: 'relative',
                                    marginTop: 'auto',
                                    marginBottom: 'auto',
                                    height: '100%',
                                }}>
                        <CardImageGallery props={data} pageProps={props} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }} sx={{ display: 'flex', paddingTop: { xs: '40px', sm: '40px', md: '0px' } }}>
                        <img loading="lazy" src={guitarServicePage}
                            style={{
                                position: 'relative',
                                margin: 'auto',
                                maxWidth: '500px',
                                width: '100%',
                            }}
                            alt="guitar service" onClick={() => handleOpenLightShot(guitarServicePage, "guitar service")} />
                    </Grid>
                </Grid>
                <TablePagination component="div"
                              count={total}
                              page={page}
                              sx={{ '>div': {
                                  alignContent: 'flex-start',
                                  flexWrap: 'wrap',
                              }}}
                              onPageChange={handleChangePage}
                              rowsPerPage={pageSize}
                              labelRowsPerPage={t('table.pagination.label_rows_per_page')}
                              labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} ${t('table.pagination.of')} ${
                                        count !== -1 ? count : `${t('table.pagination.more_than')} ${to}`
                                    }`
                              }
                              getItemAriaLabel={(type) => t(`table.pagination.aria_label_${type}`)}
                              rowsPerPageOptions={pageSizeOptions}
                              onRowsPerPageChange={handleChangePageSize} />
            </Box>
            <Footer {...props} />
        </Box>
    )
}
